import classNames from 'classnames';
import { ResetFormButton } from 'components/common-form/components/ResetFormButton';
import { IconButton } from 'components/shared/IconButton';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { ProductVariation, ProductVariationValidSyntax } from 'models/productsReferential';
import { useIntl } from 'react-intl';
import { useCloneTab, useCloseCurrentTab, useResetTab } from 'routes/essential-workspace/components/header/hooks';
import { usePinnedProducts } from 'routes/essential-workspace/components/product-selector/hooks/use-pinned';
import { selectEssentialWorkflowStatus } from 'routes/essential-workspace/state/form.selectors';
import { useEssentialWorkspaceActiveTabInfos } from 'state/workspaces/essential/hooks';

import { HeaderMoreActionsDropdown } from './more-actions';

export type HeaderActionsProps = {
  onEditTabName: Nullable<() => void>;
  onToggleRightPanel: Nullable<() => void>;
  isRightPanelOpened: boolean;
};

function useIsProductVariationSelected() {
  const activeTabId = useAssertedActiveTabId();
  const workflowStatus = useAppSelector(state => selectEssentialWorkflowStatus(state, activeTabId));
  return workflowStatus !== 'creating';
}

function convertProductVariationFromTabContentToUserPrefNaming(
  storedVariation: Partial<ProductVariationValidSyntax> | undefined,
): ProductVariation | undefined {
  const activeProductVariation =
    storedVariation?.productType && storedVariation?.productSubtype && storedVariation?.wrapper
      ? {
          productType: storedVariation.productType,
          productSubType: storedVariation.productSubtype,
          wrapper: storedVariation.wrapper,
        }
      : undefined;
  return activeProductVariation;
}

export function HeaderActions({ onEditTabName, onToggleRightPanel, isRightPanelOpened }: HeaderActionsProps) {
  const { formatMessage } = useIntl();
  const closeTab = useCloseCurrentTab();
  const { canReset, onResetTabClicked, onRefreshDefaultDatesClicked, onRefreshScheduleClicked } = useResetTab();
  const { canClone, onCloneTabClicked } = useCloneTab();

  const { isProductPinned, handlePinToggle } = usePinnedProducts('essential');

  const activeTabContent = useEssentialWorkspaceActiveTabInfos();
  const storedVariation = activeTabContent?.productVariation;

  const activeProductVariation = convertProductVariationFromTabContentToUserPrefNaming(storedVariation);

  const isProductVariationSelected = useIsProductVariationSelected();
  const isProductVariationPinned = activeProductVariation ? isProductPinned(activeProductVariation) : false;

  return (
    <div className="flex-x p-2">
      {isProductVariationSelected ? (
        <>
          <IconButton
            iconName={'auto_awesome_mosaic'}
            className={classNames('z-1 essential-header-layout-btn', {
              'essential-header-layout-btn-active': isRightPanelOpened,
            })}
            variant="outlined"
            ariaLabel={formatMessage({ id: 'essential.workspace.productLayout.btn.ariaLabel' })}
            isDisabled={!activeProductVariation}
            onClick={() => {
              if (onToggleRightPanel !== null) {
                onToggleRightPanel();
              }
            }}
          />
          <IconButton
            iconName={isProductVariationPinned ? 'star' : 'star_border'}
            className={classNames('btn-lg', { 'btn-flat-info': isProductVariationPinned })}
            ariaLabel={formatMessage({ id: 'essential.workspace.favorites.btn.ariaLabel' })}
            isDisabled={!activeProductVariation}
            onClick={() => {
              if (!activeProductVariation) {
                return;
              }

              handlePinToggle({
                productVariation: activeProductVariation,
                tabType: 'form',
              });
            }}
          />
          <IconButton
            iconName="filter_none"
            isDisabled={!canClone}
            onClick={onCloneTabClicked}
            className="btn-lg"
            ariaLabel={formatMessage({ id: 'essential.workspace.clone.tab.btn.ariaLabel' })}
          />
          <ResetFormButton
            iconName="settings_backup_restore"
            className="btn-lg"
            onResetTabClicked={onResetTabClicked}
            onRefreshDefaultDatesClicked={onRefreshDefaultDatesClicked}
            onRefreshScheduleClicked={onRefreshScheduleClicked}
            isDisabled={!canReset}
          />
        </>
      ) : (
        <>
          <IconButton iconName="close" e2e="essential-workspace-close-btn" onClick={closeTab} className="btn-lg" />
        </>
      )}
      <HeaderMoreActionsDropdown onEditTabName={onEditTabName} />
    </div>
  );
}
